import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Search, ListOrdered, Calendar, Package, TrendingDown, Plus, Trash2, UserStar, Check, ChevronsUpDown, Pencil, X } from 'lucide-react';
import { cn } from "@/lib/utils"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

// --- Helpers ---

// Formateador estático para Guaraníes (PYG)
const formatPYG = (amount) => {
  return new Intl.NumberFormat('es-PY', {
    style: 'currency',
    currency: 'PYG',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Mapeo de estilos para el Badge de estado
const statusStyles = {
  'Pendiente': 'secondary',
  'Enviada': 'default',
  'Entregado': 'success', // Asumo que tienes un estilo 'success' o puedes usar un color fijo
  'Cancelada': 'destructive',
};

/**
 * Función para obtener el estilo de Badge basado en el estado.
 * @param {string} status - El estado de la orden de compra.
 * @returns {string} La variante de Badge.
 */
const getStatusVariant = (status) => {
  switch (status) {
    case 'Pendiente':
      return 'secondary'; // Color gris/neutro
    case 'Enviada':
      return 'default'; // Color primario
    case 'Entregado':
      // Usamos un estilo que simule éxito, si no existe 'success', usamos un color fijo
      return 'bg-[#2E7D32] text-white hover:bg-[#2E7D32]/90';
    case 'Cancelada':
      return 'destructive'; // Color rojo
    default:
      return 'secondary';
  }
};

// --- Componente Principal ---

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null); // Para ver los detalles en el modal
  const [isLoading, setIsLoading] = useState(true);

  const [expensesDialogOrder, setExpensesDialogOrder] = useState(null); // Deprecated, to be removed

  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Controls Read vs Write mode in the dialog

  const [suppliers, setSuppliers] = useState([]);
  const [isSuppliersDialogOpen, setIsSuppliersDialogOpen] = useState(false);
  const [newSupplierData, setNewSupplierData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    ruc: '', // Asumimos RUC como identificador fiscal
  });

  const resetSupplierForm = () => {
    setNewSupplierData({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      ruc: '',
    });
  };
  const [availableProducts, setAvailableProducts] = useState([]);
  const [openCombobox, setOpenCombobox] = useState(false);
  const [newOrder, setNewOrder] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_invoice: '',
    items: [],
    // fixed_expenses: [], // Removed
    expected_delivery_date: null,
    // fixed_expenses: [], // Removed
    expected_delivery_date: null,
    order_date: null,
  });

  // Reception Dialog State
  const [isReceiveDialogOpen, setIsReceiveDialogOpen] = useState(false);
  const [orderToReceive, setOrderToReceive] = useState(null);
  const [receptionInvoiceNumber, setReceptionInvoiceNumber] = useState('');

  // Edit Invoice State
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editedInvoiceNumber, setEditedInvoiceNumber] = useState('');

  const { user } = useAuth();

  useEffect(() => {
    fetchPurchaseOrders();
    fetchSuppliersAndProducts();
  }, []);

  const fetchPurchaseOrders = async () => {
    setIsLoading(true);
    try {
      // Endpoint asumido: /api/v1/purchase_orders
      const response = await axios.get(`${API}/purchase_orders/`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Error al cargar órdenes de compra');
      console.error('Error fetching purchase orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliersAndProducts = async () => {
    try {
      const [suppliersResponse, productsResponse] = await Promise.all([
        axios.get(`${API}/suppliers/`),
        axios.get(`${API}/products/`),
      ]);
      setSuppliers(suppliersResponse.data);
      setAvailableProducts(productsResponse.data);
    } catch (error) {
      toast.error('Error al cargar proveedores y productos');
      console.error('Error fetching suppliers and products:', error);
    }
  };

  // Lógica de filtrado
  const filteredOrders = useMemo(() => {
    if (!searchQuery) {
      return orders;
    }
    const query = searchQuery.toLowerCase();
    return orders.filter(order =>
      order.supplier_name.toLowerCase().includes(query) ||
      order.supplier_invoice?.toLowerCase().includes(query) ||
      order.id.toLowerCase().includes(query)
    );
  }, [searchQuery, orders]);

  const isOrderDelivered = selectedOrder?.status === 'Entregado';

  const prepareEditForm = (order) => {
    setNewOrder({
      supplier_id: order.supplier_id,
      supplier_name: order.supplier_name,
      supplier_invoice: order.supplier_invoice || '',
      items: order.items.map(item => ({
        ...item,
        product_name: item.product_name,
        sku: item.sku,
        cost_pyg: Number(item.cost_pyg || 0),
        quantity: Number(item.quantity)
      })),
      expected_delivery_date: order.expected_delivery_date ? new Date(order.expected_delivery_date).toISOString().slice(0, 16) : '',
      order_date: order.order_date
    });
  };

  const handleOpenCreateAndEdit = () => {
    setSelectedOrder(null);
    setNewOrder({
      supplier_id: '',
      supplier_invoice: '',
      items: [],
      expected_delivery_date: '',
      order_date: null
    });
    setIsEditing(true);
    setIsOrderDialogOpen(true);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    // Pre-populate form data just in case user switches to edit
    prepareEditForm(order);
    setIsEditing(false); // Read-only mode initially
    setIsOrderDialogOpen(true);
  };

  const handleLoginToEditMode = () => {
    setIsEditing(true);
  }


  // Función para dar formato a las fechas
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleAddItem = (productId) => {
    const product = availableProducts.find(p => p.id === productId);
    if (!product || newOrder.items.some(item => item.product_id === productId)) {
      toast.error('Producto no encontrado o ya agregado');
      return;
    }

    const newItem = {
      product_id: product.id,
      sku: product.sku,
      product_name: product.name,
      quantity: 1,
      cost_pyg: 0, // Inicializar en 0 para ingreso manual
    };

    setNewOrder({
      ...newOrder,
      items: [...newOrder.items, newItem],
    });
  };

  const handleUpdateItem = (productId, field, value) => {
    const numericValue = ["quantity", "cost_pyg"].includes(field) ? Number(value) : value;
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.product_id === productId ? { ...item, [field]: numericValue } : item
      ),
    }));
  };

  const handleRemoveItem = (productId) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.filter(item => item.product_id !== productId),
    }));
  };



  const totalItemsCost = newOrder.items.reduce((sum, item) => sum + item.quantity * (item.cost_pyg || 0), 0);
  // El costo total de la orden se refiere a PYG
  const totalOrderCost = totalItemsCost;



  // Logic removed, merged into handleViewDetails and prepareEditForm





  const handleSaveOrder = async (e) => {
    e.preventDefault();

    if (!newOrder.supplier_id || !newOrder.expected_delivery_date || newOrder.items.length === 0) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    const supplier = suppliers.find(s => s.id === newOrder.supplier_id).name;
    const payload = {
      ...newOrder,
      supplier_name: supplier,
      total_cost_pyg: totalOrderCost,
      items: newOrder.items.map(item => ({
        ...item,
        cost_pyg: Number(item.cost_pyg),
        quantity: Number(item.quantity),
      })),
    };

    try {
      if (selectedOrder) {
        // Modo Edición
        await axios.put(`${API}/purchase_orders/${selectedOrder.id}`, payload);
        toast.success('Orden de compra actualizada exitosamente');
      } else {
        // Modo Creación
        await axios.post(`${API}/purchase_orders/`, payload);
        toast.success('Orden de compra creada exitosamente');
      }

      setNewOrder({
        supplier_id: '',
        supplier_invoice: '',
        items: [],
        expected_delivery_date: '',
        order_date: null
      });
      setSelectedOrder(null);
      setIsOrderDialogOpen(false);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error('Error al guardar la orden de compra');
      console.error('Error saving purchase order:', error);
    }
  };

  // Removed handleEditOrder separate function, logic moved to handleViewDetails + internal switch


  const handleReceiveOrder = (order) => {
    setOrderToReceive(order);
    setReceptionInvoiceNumber(order.supplier_invoice || '');
    setIsReceiveDialogOpen(true);
  };

  const handleConfirmReceive = async (e) => {
    e.preventDefault();
    if (!orderToReceive) return;

    try {
      await axios.post(`${API}/purchase_orders/${orderToReceive.id}/receive`, {
        supplier_invoice: receptionInvoiceNumber
      });
      toast.success(`Orden de compra ${orderToReceive.id.substring(0, 8)} recibida exitosamente`);
      fetchPurchaseOrders();
      setIsReceiveDialogOpen(false);
      setOrderToReceive(null);
    } catch (error) {
      toast.error(`Error al recibir la orden de compra`);
      console.error('Error receiving purchase order:', error);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!selectedOrder) return;
    try {
      await axios.patch(`${API}/purchase_orders/${selectedOrder.id}/invoice`, {
        supplier_invoice: editedInvoiceNumber
      });
      toast.success('Número de factura actualizado');

      // Update local state and list
      setSelectedOrder(prev => ({ ...prev, supplier_invoice: editedInvoiceNumber }));
      setOrders(prevOrders => prevOrders.map(o =>
        o.id === selectedOrder.id ? { ...o, supplier_invoice: editedInvoiceNumber } : o
      ));
      setIsEditingInvoice(false);
    } catch (error) {
      toast.error('Error al actualizar factura');
      console.error('Error updating invoice:', error);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(`¿Estás seguro de cancelar la orden de compra ${orderId.substring(0, 8)}?`)) {
      return;
    }
    try {
      await axios.post(`${API}/purchase_orders/${orderId}/cancel`);
      toast.success(`Orden de compra ${orderId.substring(0, 8)} cancelada exitosamente`);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error(`Error al cancelar la orden de compra ${orderId.substring(0, 8)}`);
      console.error('Error canceling purchase order:', error);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();

    try {
      // Endpoint asumido: POST /api/v1/suppliers
      await axios.post(`${API}/suppliers/`, newSupplierData);
      toast.success('Proveedor creado exitosamente.');
      resetSupplierForm();
      fetchSuppliersAndProducts(); // Recarga la lista de proveedores
      // No cerramos el modal, permitiendo que el usuario siga agregando
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el proveedor.');
      console.error('Error creating supplier:', error);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('¿Está seguro de eliminar este proveedor? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      // Endpoint asumido: DELETE /api/v1/suppliers/{id}
      await axios.delete(`${API}/suppliers/${supplierId}`);
      toast.success('Proveedor eliminado exitosamente.');
      fetchSuppliersAndProducts(); // Recarga la lista
    } catch (error) {
      toast.error('Error al eliminar proveedor. Podría tener órdenes de compra asociadas.');
      console.error('Error deleting supplier:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Cargando órdenes de compra...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2" data-font="display">
          <ListOrdered className="size-6 text-muted-foreground" /> Órdenes de Compra
        </h1>
        {/* Aquí podrías colocar un botón para crear una nueva orden de compra (fuera del alcance actual) */}
        <div className="flex gap-2">
          {/* --- DIALOG DE GESTIÓN DE PROVEEDORES --- */}
          <Dialog open={isSuppliersDialogOpen} onOpenChange={setIsSuppliersDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <UserStar className="mr-2 size-4" /> Gestionar Proveedores
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Administración de Proveedores</DialogTitle>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Panel Izquierdo: Formulario de Creación */}
                <Card className="p-4">
                  <CardTitle className="text-lg mb-4">📝 Crear Nuevo Proveedor</CardTitle>
                  <form onSubmit={handleCreateSupplier} className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="s_name">Nombre *</Label>
                      <Input id="s_name" value={newSupplierData.name} onChange={(e) => setNewSupplierData({ ...newSupplierData, name: e.target.value })} required />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor="s_ruc">RUC</Label>
                        <Input id="s_ruc" value={newSupplierData.ruc} onChange={(e) => setNewSupplierData({ ...newSupplierData, ruc: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="s_contact">Contacto</Label>
                        <Input id="s_contact" value={newSupplierData.contact_person} onChange={(e) => setNewSupplierData({ ...newSupplierData, contact_person: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="s_email">Email</Label>
                      <Input id="s_email" type="email" value={newSupplierData.email} onChange={(e) => setNewSupplierData({ ...newSupplierData, email: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="s_phone">Teléfono</Label>
                      <Input id="s_phone" value={newSupplierData.phone} onChange={(e) => setNewSupplierData({ ...newSupplierData, phone: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="s_address">Dirección</Label>
                      <Input id="s_address" value={newSupplierData.address} onChange={(e) => setNewSupplierData({ ...newSupplierData, address: e.target.value })} />
                    </div>
                    <Button type="submit" className="w-full bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
                      Guardar Proveedor
                    </Button>
                  </form>
                </Card>

                {/* Panel Derecho: Lista de Proveedores */}
                <Card className="p-4">
                  <CardTitle className="text-lg mb-4">📋 Lista de Proveedores Existentes ({suppliers.length})</CardTitle>
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nombre</TableHead>
                          <TableHead>RUC</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {suppliers.map((s) => (
                          <TableRow key={s.id}>
                            <TableCell className="font-medium">{s.name}</TableCell>
                            <TableCell>{s.ruc || '-'}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteSupplier(s.id)}
                              >
                                <Trash2 className="size-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {suppliers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground">
                              No hay proveedores registrados.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setIsSuppliersDialogOpen(false)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={isOrderDialogOpen} onOpenChange={(open) => {
            setIsOrderDialogOpen(open);
            if (!open) {
              // Reset state on close
              setSelectedOrder(null);
              setIsEditing(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenCreateAndEdit} className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
                <Plus className="mr-2 size-4" /> Nueva Orden
              </Button>
            </DialogTrigger>

            {/* --- DIALOG CONTENT --- */}
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-font="display">
                  {isEditing
                    ? (selectedOrder ? `Editar Orden #${selectedOrder.id.substring(0, 8)}` : 'Crear Nueva Orden de Compra')
                    : `Detalles de la Orden #${selectedOrder?.id?.substring(0, 8)}`
                  }
                </DialogTitle>
                {!isEditing && <DialogDescription>Detalles de la orden de compra seleccionada.</DialogDescription>}
              </DialogHeader>

              {/* READ ONLY MODE */}
              {!isEditing && selectedOrder && (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4 border-b pb-4">
                    <InfoItem icon={<Package className="size-4" />} title="Proveedor" value={selectedOrder.supplier_name} />
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <ListOrdered className="size-4" /> Factura
                      </div>
                      <p className="font-semibold text-gray-900">{selectedOrder.supplier_invoice || 'N/A'}</p>
                    </div>
                    <InfoItem icon={<Calendar className="size-4" />} title="Fecha de Orden" value={formatDate(selectedOrder.order_date)} />
                    <InfoItem icon={<Calendar className="size-4" />} title="Entrega Estimada" value={formatDate(selectedOrder.expected_delivery_date)} isDelayed={selectedOrder.is_delayed} />
                    <InfoItem icon={<Calendar className="size-4" />} title="Fecha de Recepción" value={formatDate(selectedOrder.reception_date)} />
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-muted-foreground">Estado</div>
                      <Badge variant={getStatusVariant(selectedOrder.status)} className={getStatusVariant(selectedOrder.status)}>
                        {selectedOrder.status}
                      </Badge>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold" data-font="display">Productos de la Orden</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Producto</TableHead>
                          <TableHead className="text-right">Cantidad</TableHead>
                          <TableHead className="text-right">Costo Unitario (Gs)</TableHead>
                          <TableHead className="text-right">Subtotal (Gs)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-sm">{item.sku}</TableCell>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatPYG(item.cost_pyg)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatPYG(item.quantity * item.cost_pyg)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex flex-col items-end gap-1 pt-4">
                    <div className="text-xl font-bold flex gap-4">
                      <span data-font="display">Costo Productos:</span>
                      <span data-font="display">{formatPYG(selectedOrder.total_cost_pyg)}</span>
                    </div>
                  </div>

                  <DialogFooter className="pt-4 flex justify-between">
                    {/* Acciones en Modo Lectura */}
                    <div className="flex gap-2">
                      {/* Botón Editar habilitado para Admin y no Cancelado */}
                      {user?.role === 'Admin' && selectedOrder.status !== 'Cancelado' && (
                        <Button variant="outline" onClick={handleLoginToEditMode}>
                          <Pencil className="mr-2 size-4" /> Editar Orden
                        </Button>
                      )}

                      {(selectedOrder.status === 'Pendiente' || selectedOrder.status === 'Ordenado' || selectedOrder.status === 'Enviada') && (
                        <Button
                          variant="default"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            handleReceiveOrder(selectedOrder);
                            setIsOrderDialogOpen(false); // Close details when opening reception
                          }}
                        >
                          Confirmar Recepción
                        </Button>
                      )}
                    </div>

                    <Button variant="ghost" onClick={() => setIsOrderDialogOpen(false)}>
                      Cerrar
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* EDIT/CREATE MODE */}
              {isEditing && (
                <form onSubmit={handleSaveOrder} className="space-y-6">


                  {/* Bloque de Información General */}
                  <Card className="p-4 space-y-4">
                    <h3 className="text-lg font-bold">Detalles del Proveedor y Entrega</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="supplier_id">Proveedor *</Label>
                        <Select
                          value={newOrder.supplier_id}
                          onValueChange={(val) => setNewOrder({ ...newOrder, supplier_id: val })}
                        >
                          <SelectTrigger id="supplier_id">
                            <SelectValue placeholder="Seleccionar proveedor" />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="expected_delivery_date">Fecha de Entrega Estimada *</Label>
                        <Input
                          id="expected_delivery_date"
                          type="datetime-local"
                          value={newOrder.expected_delivery_date}
                          onChange={(e) => setNewOrder({ ...newOrder, expected_delivery_date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="supplier_invoice">Factura del Proveedor (Opcional)</Label>
                        <Input
                          id="supplier_invoice"
                          value={newOrder.supplier_invoice}
                          onChange={(e) => setNewOrder({ ...newOrder, supplier_invoice: e.target.value })}
                          placeholder="Número de factura"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Bloque de Ítems */}
                  <Card className="p-4 space-y-4">
                    <h3 className="text-lg font-bold flex justify-between items-center">
                      Ítems de la Orden
                      <div className="w-1/3">
                        {!isOrderDelivered && (
                          <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={openCombobox}
                                className="w-full justify-between"
                              >
                                Añadir Producto...
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[400px] p-0">
                              <Command>
                                <CommandInput placeholder="Buscar producto por nombre o SKU..." />
                                <CommandList>
                                  <CommandEmpty>No se encontró el producto.</CommandEmpty>
                                  <CommandGroup>
                                    {availableProducts
                                      .filter(p => !newOrder.items.some(item => item.product_id === p.id))
                                      .map((product) => (
                                        <CommandItem
                                          key={product.id}
                                          value={`${product.name} ${product.sku}`}
                                          onSelect={() => {
                                            handleAddItem(product.id)
                                            setOpenCombobox(false)
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              newOrder.items.some(item => item.product_id === product.id) ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {product.name} ({product.sku})
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}
                        {isOrderDelivered && (
                          <div className="text-sm text-yellow-600 font-medium text-right">
                            Edición de productos bloqueada (Orden Entregada)
                          </div>
                        )}
                      </div>

                    </h3>

                    <div className="overflow-x-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Producto</TableHead>
                            <TableHead className="w-[120px] text-right">Cantidad *</TableHead>
                            <TableHead className="w-[150px] text-right">Costo (Gs) *</TableHead>
                            <TableHead className="w-[150px] text-right">Subtotal</TableHead>
                            <TableHead className="w-[50px] text-right">Eliminar</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {newOrder.items.map((item) => (
                            <TableRow key={item.product_id}>
                              <TableCell className="font-medium">
                                {item.product_name} <span className="text-xs text-muted-foreground">({item.sku})</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateItem(item.product_id, 'quantity', e.target.value)}
                                  min="1"
                                  required
                                  className="text-right"
                                  disabled={isOrderDelivered}
                                />

                              </TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  step="1"
                                  value={item.cost_pyg}
                                  onChange={(e) => handleUpdateItem(item.product_id, 'cost_pyg', e.target.value)}
                                  min="0"
                                  required
                                  className="text-right"
                                  disabled={isOrderDelivered}
                                />

                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatPYG(item.quantity * item.cost_pyg)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveItem(item.product_id)}
                                  disabled={isOrderDelivered}
                                >
                                  {isOrderDelivered ? <X className="size-4 text-gray-400" /> : <Trash2 className="size-4 text-destructive" />}
                                </Button>

                              </TableCell>
                            </TableRow>
                          ))}
                          {newOrder.items.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                                Añada productos a la orden.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>



                  <DialogFooter className="flex justify-between items-center pt-4">
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-xl font-bold flex gap-4">
                        <span data-font="display">Costo Productos:</span>
                        <span data-font="display" className="text-green-600">{formatPYG(totalOrderCost)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" onClick={() => setIsCreateDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]" disabled={newOrder.items.length === 0}>
                        {selectedOrder ? 'Actualizar Orden' : 'Crear Orden'}
                      </Button>
                    </div>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Search className="size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por proveedor, factura o ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">ID</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Factura</TableHead>
                  <TableHead>Fecha de Orden</TableHead>
                  <TableHead>Entrega Estimada</TableHead>
                  <TableHead>Costo Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium text-xs">{order.id.substring(0, 8)}...</TableCell>
                    <TableCell>{order.supplier_name}</TableCell>
                    <TableCell>{order.supplier_invoice || '-'}</TableCell>
                    <TableCell>{formatDate(order.order_date)}</TableCell>
                    <TableCell>
                      <div className={`flex items-center ${order.is_delayed ? 'text-red-500 font-medium' : ''}`}>
                        {order.is_delayed && <TrendingDown className="size-4 mr-1" />}
                        {formatDate(order.expected_delivery_date)}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">{formatPYG(order.total_cost_pyg)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(order.status)} className={getStatusVariant(order.status)}>
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {(order.status === 'Pendiente' || order.status === 'Ordenado' || order.status === 'Enviado') && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 mr-2"
                          onClick={() => handleReceiveOrder(order)}
                        >
                          Recepcionar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(order)}
                      >

                        Ver Detalles
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/fixed-expenses?purchase_order_id=${order.id}`)}
                        className="text-xs ml-2"
                      >
                        Gastos
                      </Button>

                    </TableCell>
                  </TableRow>
                ))}
                {filteredOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No se encontraron órdenes de compra
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>


      {/* Modal de Recepción */}
      <Dialog open={isReceiveDialogOpen} onOpenChange={setIsReceiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Recepción</DialogTitle>
            <DialogDescription>
              Ingrese el número de factura para confirmar la recepción de la orden <strong>{orderToReceive?.id?.substring(0, 8)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfirmReceive} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reception_invoice">Número de Factura</Label>
              <Input
                id="reception_invoice"
                value={receptionInvoiceNumber}
                onChange={(e) => setReceptionInvoiceNumber(e.target.value)}
                placeholder="Ej. 001-001-1234567"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsReceiveDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">Confirmar y Recibir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// Componente auxiliar para mostrar la información del modal
const InfoItem = ({ icon, title, value, isDelayed = false }) => (
  <div className="space-y-1">
    <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">{icon} {title}</div>
    <p className={`font-semibold ${isDelayed ? 'text-red-500' : 'text-gray-900'}`}>{value}</p>
  </div>
);